import React, { useState, useRef, useEffect } from 'react';
import { Upload, RotateCcw, Trash, ImagePlus, BadgeInfo, ChevronDown, ChevronUp, ArrowRight } from 'lucide-react';
import './PetCustomizer.scss';
import upload from '/icons/upload-white.svg';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination } from 'swiper/modules';
import { useSelector, useDispatch } from 'react-redux';
import { useAppNavigate } from '../../hooks/useAppNavigate';
import useFileHandlers from '../../hooks/useFileHandlers';
import { useAffiliate } from '../../hooks/useAffiliate';
import Button from '../ui/Button';
import FramesModal from './FramesModal';
import { Tooltip } from 'react-tooltip';
import { toast } from 'react-toastify';
import {
  drawCanvas
} from './functions/canvasRenderer';
import {
  loadImagesFromDB
} from './functions/loadImagesFromIndexedDB';
import {
  handlePointerDown,
  handlePointerMove,
  handlePointerUp
} from './functions/pointerHandlers';
import {
  downloadCanvas
} from './functions/downloadCanvas';
import {
  saveCanvas
} from './functions/saveCanvas';
import { CANVAS_HEIGHT, CANVAS_WIDTH, CUSTOMIZER_MAX_IMAGES_COUNT } from '../../config/config';
import dog_icon from '/icons/dog-icon.svg';
import cat_icon from '/icons/cat-icon.svg';
import {
  duplicateImageAsync,
  setSelectedFrame,
  setSelectedFrameCategory,
  setIncludePetName,
  setPetName,
  setPetType,
  selectUploadedImageIds,
  removeImage,
  moveImage,
  rotateImage,
  scaleImage,
  changeCustomizerStep,
  setCustomizerStep
} from '../../store/customizerSlice';
import { deleteImageFromDB } from '../../utils/indexedDB';
import { saveCanvasMobile } from './functions/saveCanvasMobile';
import { scrollToTop } from '../../utils/scrollToTop';


const MobilePetCustomizer = ({ subscriptionPage = false, setSubscriptionPageStep, onSubmit }) => {
  const dispatch = useDispatch();
  const navigate = useAppNavigate();
  const { affiliate } = useAffiliate();
  const { handleFileSelect, handleDrop } = useFileHandlers();

  const canvasWrapperRef = useRef(null);
  const canvasRef = useRef(null);
  const overlayRef = useRef(null);
  const frameSelectionRef = useRef(null);
  const innerFrameContentRef = useRef(null);
  const framesSwiperRef = useRef(null);
  const imageCache = useRef(new Map());
  const petSelection = useRef(null);
  const touchesRef = useRef({
    initialDistance: null,
    initialScale: null,
  });

  const [selectedImageIndex, setSelectedImageIndex] = useState(null);
  const [dragStart, setDragStart] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [staticBg, setStaticBg] = useState(null);
  const [frameImage, setFrameImage] = useState(null);
  const [imageElements, setImageElements] = useState(new Map());
  const [frameSectionHeight, setFrameSectionHeight] = useState(null);
  const [isFramesModalActive, setFramesModalActive] = useState(false);
  const [isPetSelectionExpanded, setIsPetSelectionExpanded] = useState(true);
  const [isFrameSelectionExpanded, setIsFrameSelectionExpanded] = useState(true);
  const [notSelectedKey, setNotSelectedKey] = useState(0);
  const [runOnRender, setRunOnRender] = useState(false);
  const [hideCanvas, setHideCanvas] = useState(false);

  const includePetName = useSelector((state) => state.customizer.includePetName);
  const petName = useSelector((state) => state.customizer.petName);
  const petType = useSelector((state) => state.customizer.petType);
  const images = useSelector((state) => state.customizer.images);
  const frames = useSelector((state) => state.customizer.frames);
  const framesCategories = useSelector((state) => state.customizer.framesCategories);
  const selectedFrame = useSelector((state) => state.customizer.selectedFrame);
  const selectedFrameCategory = useSelector((state) => state.customizer.selectedFrameCategory);
  const uploadedImageIds = useSelector(selectUploadedImageIds);
  const cartItems = useSelector((state) => state.cart.cartItems);
  const customizerStep = useSelector((state) => state.customizer.customizerStep);
  const filteredFrames = frames.filter((f) => f.category === selectedFrameCategory);



  useEffect(() => {
    if (framesSwiperRef.current) {
      framesSwiperRef.current.slideTo(0, 0);
    }
  }, [selectedFrameCategory]);

  useEffect(() => {
    if (!runOnRender) {
      setRunOnRender(true);
      return;
    }
    if (!frameSelectionRef.current || !innerFrameContentRef.current) return;
    requestAnimationFrame(() => {
      const heightToSet = isFrameSelectionExpanded
        ? `${innerFrameContentRef.current.offsetHeight + 10}px`
        : "0px";
      setFrameSectionHeight(heightToSet);
    });
  }, [filteredFrames.length, selectedFrameCategory, isFrameSelectionExpanded]);

  useEffect(() => {
    if (images.length > 0) {
      const lastImage = images[images.length - 1];
      if (selectedImageIndex === null || images[selectedImageIndex]?.id !== lastImage.id) {
        setSelectedImageIndex(images.length - 1);
      }
    }
  }, [images.length]);

  useEffect(() => {
    if (staticBg) {
      setSelectedImageIndex((prev) => prev);
    }
  }, [staticBg]);

  useEffect(() => {
    const img = new Image();
    img.src = "/images/background.png";
    img.crossOrigin = "anonymous";
    img.onload = () => {
      setStaticBg(img);
    };
  }, []);

  useEffect(() => {
    document.fonts.ready.then(() => {
      setTimeout(() => {
        setPetName((prev) => prev + " ");
        setTimeout(() => setPetName((prev) => prev.trim()), 10);
      }, 50);
    });
  }, []);

  useEffect(() => {
    const img = new Image();
    img.src = selectedFrame.url;
    img.onload = () => setFrameImage(img);
  }, [selectedFrame]);

  useEffect(() => {
    drawCanvas({
      canvas: canvasRef.current,
      images,
      staticBg,
      frameImage,
      imageCache,
      selectedFrame,
      includePetName,
      petName,
      isDragging,
    });
  }, [images, frameImage, staticBg, imageElements, selectedFrame, petName, includePetName, isDragging]);

  useEffect(() => {
    const load = async () => {
      if (images.length === 0) return;
      const newImages = await loadImagesFromDB(images, imageCache);
      if (newImages.size > 0) {
        setImageElements((prev) => new Map([...prev, ...newImages]));
      }
    };
    load();
  }, [images.length]);

  useEffect(() => {
    const disableScroll = (e) => {
      if (e.target.closest('.customizer-outer-container')) {
        e.preventDefault();
      }
    };

    if (isDragging) {
      document.body.style.overflow = "hidden";
      document.addEventListener("touchmove", disableScroll, { passive: false });
    } else {
      document.body.style.overflow = "";
      document.removeEventListener("touchmove", disableScroll);
    }

    return () => {
      document.body.style.overflow = "";
      document.removeEventListener("touchmove", disableScroll);
    };
  }, [isDragging]);


  const pointerDownHandler = (e) => handlePointerDown(e, canvasRef, images, setSelectedImageIndex, setDragStart, setIsDragging, CANVAS_WIDTH, CANVAS_HEIGHT, touchesRef);
  const pointerMoveHandler = (e) => handlePointerMove(e, isDragging, selectedImageIndex, canvasRef, images, dragStart, dispatch, moveImage, CANVAS_WIDTH, CANVAS_HEIGHT, touchesRef);
  const pointerUpHandler = () => handlePointerUp(setDragStart, setIsDragging, touchesRef);

  const rotateImageRight = () => {
    if (selectedImageIndex !== null) {
      dispatch(rotateImage({ id: images[selectedImageIndex].id, angle: 15 }));
    }
  };

  const rotateImageLeft = () => {
    if (selectedImageIndex !== null) {
      dispatch(rotateImage({ id: images[selectedImageIndex].id, angle: -15 }));
    }
  };

  const deleteImageHandler = () => {
    if (selectedImageIndex !== null) {
      dispatch(removeImage(images[selectedImageIndex].id));
      deleteImageFromDB("uploadedImages", images[selectedImageIndex].id);
      setSelectedImageIndex(null);
    }
  };

  const scaleImageHandler = (newScale) => {
    if (selectedImageIndex !== null) {
      dispatch(scaleImage({ id: images[selectedImageIndex].id, scale: newScale }));
    }
  };



  const saveAndRedirect = () => {
    saveCanvas({
      petType,
      setNotSelectedKey,
      toast,
      cartItems,
      canvasRef,
      uploadedImageIds,
      petName,
      selectedFrame,
      navigate,
      setStaticBg,
      image: images[selectedImageIndex],
      redirectTo: !subscriptionPage ? '/product-select' : null
    });
    if (subscriptionPage && setSubscriptionPageStep && petType) {
      setSubscriptionPageStep(4);
      if (onSubmit) {
        onSubmit();
      }
    }
  };


  return (
    <div className='relative w-full'>
      {affiliate && (
        <img
          src={affiliate.logo}
          alt={`${affiliate.name} logo`}
          className="affiliate-logo-customizer"
        />
      )}
      <div className={`full-width customizer-outer-container flex justify-center ${affiliate ? 'affiliate-margin' : ''}`}>
        <div className="bg-white rounded-20 box-shadow contained-width customizer-container overflow-hidden">

          {/* Pet Type Selection */}
          <div className={`customizer-container-inner mb-3 fade-step ${customizerStep !== 2 ? 'hidden' : ''}`}>
            <div className="customizer-controls-container gap-2 md:gap-5">
              <div
                key={!petType ? `pet-type-${notSelectedKey}` : 'pet-type'}
                className={`customizer-controls ${(!petType && notSelectedKey !== 0) ? 'not-selected' : ''}`}
              >
                <div className="flex items-center justify-center gap-2 w-full">
                  <label className="block font-medium text-gray-700">Choose Your Pet</label>
                </div>

                <div
                  ref={petSelection}
                  data-ignore-image-deselect
                >
                  <div className="flex gap-4 mt-4 justify-center">
                    <button
                      onClick={() => dispatch(setPetType("Dog"))}
                      className={`pet-selection-button rounded-10 border-2 gap-2 w-24 p-2 flex flex-col items-center justify-center
                      ${petType === "Dog" ? "selected" : ""}`}
                    >
                      <img src={dog_icon} alt="" />
                      <span className="">Dog</span>
                    </button>

                    <button
                      onClick={() => dispatch(setPetType("Cat"))}
                      className={`pet-selection-button rounded-10 border-2 gap-2 w-24 p-2 flex flex-col items-center justify-center
                      ${petType === "Cat" ? "selected" : ""}`}
                    >
                      <img src={cat_icon} alt="" />
                      <span className="">Cat</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Pet name */}
          <div className={`customizer-container-inner mb-3 fade-step ${customizerStep !== 2 ? 'hidden' : ''}`}>

            <div className="customizer-controls-container gap-2 md:gap-5">
              <div className="customizer-controls pet-name-container">
                <div className='flex flex-col gap-2 w-full items-center' data-ignore-image-deselect>
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={includePetName}
                      onChange={() => dispatch(setIncludePetName(!includePetName))}
                      className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                    />
                    <span className="font-medium text-gray-700 cursor-pointer">Include Pet Name on Label</span>
                  </label>

                  {includePetName && (
                    <div className="flex flex-col gap-2 w-full items-center">
                      <input
                        value={petName}
                        type="text"
                        maxLength="20"
                        placeholder="Pet Name"
                        onChange={(e) => dispatch(setPetName(e.target.value))}
                        className="pet-name-input"
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>


          <FramesModal
            isOpen={isFramesModalActive}
            onClose={() => setFramesModalActive(false)}
            selectedImage={images[selectedImageIndex]}
            imageCache={imageCache}
          />

          <div className="customizer-container-inner p-5 md:p-10 gap-3 md:gap-10">

            {/* Controls container */}
            <div className="customizer-controls-container gap-2 md:gap-5">

              {/* {(customizerStep === 1 && images?.length > 0 && images?.length < CUSTOMIZER_MAX_IMAGES_COUNT && selectedImageIndex === null) && (
                <>
                  <div className="hint "> <BadgeInfo size={19} /> Click the image to edit it</div>
                  <button
                    onClick={() => document.getElementById('fileInput').click()}
                    className="upload-btn-outlined"
                  >
                    <ImagePlus size={19} /> Upload New Image
                  </button>
                </>
              )} */}

              <div className={`flex items-center gap-2 customizer-controls customizer-image-controls ${customizerStep === 1 && images.length > 0 ? '' : 'hidden'}`} data-ignore-image-deselect>
                <div className='flex flex-col gap-2'>
                  <label className="block  font-medium text-gray-700">
                    Image Controls
                  </label>

                  <div>
                    <button
                      onClick={rotateImageLeft}
                      className="p-3 hover:bg-gray-100 rounded"
                      data-tooltip-id="rotate-left"
                    >
                      <RotateCcw size={20} />
                    </button>
                    <Tooltip className="controls-tooltip" opacity="1" id="rotate-left" content="Rotate Left" place="top" />

                    <button
                      onClick={rotateImageRight}
                      className="p-3 hover:bg-gray-100 rounded"
                      data-tooltip-id="rotate-right"
                    >
                      <RotateCcw size={20} className="transform scale-x-[-1]" />
                    </button>
                    <Tooltip className="controls-tooltip" opacity="1" id="rotate-right" content="Rotate Right" place="top" />

                    <button
                      onClick={deleteImageHandler}
                      className="p-3 hover:bg-gray-100 rounded"
                      data-tooltip-id="delete"
                    >
                      <Trash size={20} />
                    </button>
                    <Tooltip className="controls-tooltip" opacity="1" id="delete" content="Delete Image" place="top" />

                  </div>
                </div>
                {selectedImageIndex !== null && images[selectedImageIndex] && (
                  <div className="mt-4 slider-input">
                    <div
                      className="tooltip"
                      style={{
                        left: `${(
                          (images[selectedImageIndex].scale - 0.4) /
                          (images[selectedImageIndex].maxScale - 0.4)
                        ) * 100}%`,
                      }}
                    >
                      {`${Math.round(images[selectedImageIndex].width * images[selectedImageIndex].scale)} x ${Math.round(
                        images[selectedImageIndex].height * images[selectedImageIndex].scale
                      )}px`}
                    </div>
                    <input
                      type="range"
                      min={0.5}
                      max={images[selectedImageIndex].maxScale}
                      step="0.1"
                      value={images[selectedImageIndex].scale}
                      onChange={(e) =>
                        scaleImageHandler(parseFloat(e.target.value))
                      }
                      className="w-full"
                    />
                  </div>
                )}
              </div>

              {/* Frame select */}
              <div className={`customizer-container-inner mb-3 fade-step ${customizerStep !== 1 ? 'hidden' : ''}`}>
                <div className="customizer-controls-container">

                  <div className="customizer-controls">
                    <div className="flex items-center justify-center gap-2 w-full">
                      <label className="block font-medium text-gray-700">Choose Your Background</label>
                    </div>

                    <div
                      ref={frameSelectionRef}
                      data-ignore-image-deselect
                    >
                      <div ref={innerFrameContentRef}>
                        {/* Category Filters */}
                        <div className="flex gap-2 justify-center flex-wrap mt-2">
                          {framesCategories.map((category) => (
                            <button
                              key={category}
                              onClick={() => dispatch(setSelectedFrameCategory(category))}
                              className={`frame-category px-2 py-1 rounded-10 transition ${selectedFrameCategory === category ? "selected" : ""
                                }`}
                            >
                              {category}
                            </button>
                          ))}
                        </div>

                        <div className="flex flex-col items-center justify-center gap-4 mt-3">
                          <Swiper
                            modules={[Pagination]}
                            onSwiper={(swiper) => {
                              framesSwiperRef.current = swiper;
                            }}
                            pagination={{ el: ".frames-pagination", clickable: true }}
                            spaceBetween={10}
                            slidesPerView="auto"
                            slidesPerGroup={3}
                          >
                            {filteredFrames.map((bg, index) => (
                              <SwiperSlide key={index}>
                                <button
                                  onClick={() => dispatch(setSelectedFrame(bg))}
                                  className={`relative bg-buttons w-full rounded-10 overflow-hidden border-2 ${selectedFrame.url === bg.url ? "selected" : ""}`}
                                >
                                  <img src={bg.url} alt={`Frame ${index + 1}`} className="relative w-full h-full object-cover z-10" />

                                  {/* Uploaded image */}
                                  {images[selectedImageIndex] && imageCache.current.has(images[selectedImageIndex].id) && (
                                    <img
                                      src={imageCache.current.get(images[selectedImageIndex].id).src}
                                      alt="Uploaded Image"
                                      className="absolute z-0 inset-0 w-full h-full object-cover opacity-100"
                                    />
                                  )}
                                </button>
                              </SwiperSlide>
                            ))}
                          </Swiper>
                          <div className="frames-pagination" />
                        </div>

                        <div className='flex justify-center mt-3'>
                          <button onClick={() => setFramesModalActive(true)} className="frames-modal-btn">
                            Browse All
                          </button>
                        </div>

                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex gap-3 justify-between items-center" data-ignore-image-deselect>
                {/* <Button
                onClick={downloadCanvas}
                type="button"
                variant="icon"
                data-tooltip-id="download-result"
                className="download-result-btn"
                disabled={images.length === 0}
              >
                <Download size={26} />
              </Button> */}

                <Tooltip className="controls-tooltip" opacity="1" id="download-result" content="Download Result" place="top" />
                <Button
                  onClick={() => {
                    if (customizerStep === 1) {
                      dispatch(setCustomizerStep(2));
                      if (!subscriptionPage && !onSubmit) {
                        scrollToTop();
                      } else {
                        onSubmit();
                      }
                    } else {
                      setHideCanvas(true);
                      saveAndRedirect();
                      if (!subscriptionPage && !onSubmit) {
                        scrollToTop();
                      } else {
                        onSubmit();
                      }
                    }
                  }}
                  type="button"
                  variant="filled"
                  className="justify-between mt-1"
                  disabled={images.length === 0}
                >
                  Next Step<ArrowRight size={30} />
                </Button>
              </div>

            </div>


            {/* Canvas area */}
            <div className="customizer-content"
              style={{ pointerEvents: customizerStep === 2 ? 'none' : '' }}
            >
              <div
                ref={canvasWrapperRef}
                className={`${customizerStep === 1 ? 'border-dashed' : ''} ${(customizerStep === 2 && !includePetName) ? 'hidden' : ''} ${hideCanvas ? 'opacity-0' : ''} relative border-3 rounded-20 flex items-center justify-center overflow-hidden`}
                style={{
                  width: '100%',
                  aspectRatio: '3 / 4',
                  borderColor: 'var(--purple)',
                  cursor: isDragging ? 'grabbing' : 'pointer'
                }}
                onDrop={handleDrop}
                onDragOver={(e) => e.preventDefault()}
                onMouseDown={pointerDownHandler}
                onMouseMove={pointerMoveHandler}
                onMouseUp={pointerUpHandler}
                onTouchStart={pointerDownHandler}
                onTouchMove={pointerMoveHandler}
                onTouchEnd={pointerUpHandler}
              >
                <canvas
                  ref={canvasRef}
                  style={{ width: '100%', height: '100%' }}
                  width={CANVAS_WIDTH}
                  height={CANVAS_HEIGHT}
                />
                <canvas
                  ref={overlayRef}
                  style={{
                    width: '100%',
                    height: '100%',
                    position: 'absolute',
                    zIndex: 4,
                    pointerEvents: 'none',
                  }}
                  width={CANVAS_WIDTH}
                  height={CANVAS_HEIGHT}
                />

                {!images.length && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center upload-container">
                    <Button
                      onClick={() => document.getElementById('fileInput').click()}
                      type="button"
                      variant="filled"
                      color="secondary"
                      className="fit-width flex gap-3"
                    >
                      <img src={upload} alt="" />
                      Select Image
                    </Button>
                  </div>
                )}

              </div>
            </div>
          </div>

          <input
            id="fileInput"
            type="file"
            accept="image/png, image/jpeg, image/webp"
            className="hidden"
            onChange={handleFileSelect}
          />

        </div>

      </div>
    </div>

  );
};

export default MobilePetCustomizer;

